/**
 * React Query hook for Locations with Pagination Support
 * Designed for use with react-select-async-paginate
 * Provides automatic caching, background sync, and proper pagination handling
 * Filters for event and court locations
 */

import { useQuery } from '@tanstack/react-query';
import { listContacts } from 'services/lead/leadServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches locations (contacts) with pagination tokens
 *
 * @param {Object} options - Query options
 * @param {number} options.pageSize - Number of items per page (default: 20)
 * @param {string} options.pagination_token - Token for pagination
 * @param {string} options.searchQuery - Search query for filtering
 * @param {string} options.contactStatus - Contact status filter (default: 'ACTIVE')
 * @param {boolean} options.is_court_location - Include court locations (default: true)
 * @param {boolean} options.is_event_location - Include event locations (default: true)
 * @returns {Promise<Object>} - { options, hasMore, additional: { nextToken } }
 */
const fetchLocationsPage = async ({
  pageSize = 20,
  pagination_token = '',
  searchQuery = '',
  contactStatus = 'ACTIVE',
  is_court_location = true,
  is_event_location = true,
}) => {
  try {
    const response = await listContacts({
      contactType: '',
      contactStatus,
      keyWord: searchQuery,
      is_court_location,
      is_event_location,
      pageLimit: pageSize,
      pagination_token,
      pagination_direction: 'forward',
      excludeFromLoading: true,
    });

    const contacts = response?.data?.contacts || [];
    const nextToken = response?.data?.next_token || null;

    // Transform to react-select format
    const options = contacts.map((contact) => ({
      label: contact.title || contact.office_name,
      value: contact.contact_id || contact.office_location_id,
      ...contact, // Include full object for additional data access
    }));

    return {
      options,
      hasMore: nextToken !== null && nextToken !== undefined && nextToken !== '',
      additional: {
        nextToken: nextToken || '',
      },
    };
  } catch (error) {
    console.error('Error fetching locations page:', error);
    throw error;
  }
};

/**
 * Hook to fetch paginated locations
 * Compatible with react-select-async-paginate
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query for filtering
 * @param {string} params.pagination_token - Token for current page
 * @param {number} params.pageSize - Number of items per page (default: 20)
 * @param {string} params.contactStatus - Contact status filter (default: 'ACTIVE')
 * @param {boolean} params.is_court_location - Include court locations (default: true)
 * @param {boolean} params.is_event_location - Include event locations (default: true)
 * @param {boolean} params.enabled - Enable/disable query (default: true)
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export const useLocationsPaginated = ({
  searchQuery = '',
  pagination_token = '',
  pageSize = 20,
  contactStatus = 'ACTIVE',
  is_court_location = true,
  is_event_location = true,
  enabled = true,
} = {}) => {
  const queryKey = queryKeys.locations.paginated({
    searchQuery,
    pagination_token,
    pageSize,
    contactStatus,
    is_court_location,
    is_event_location,
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchLocationsPage({
        pageSize,
        pagination_token,
        searchQuery,
        contactStatus,
        is_court_location,
        is_event_location,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    data: data || { options: [], hasMore: false, additional: { nextToken: '' } },
    isLoading,
    error,
    refetch,
  };
};

/**
 * Load options function for react-select-async-paginate
 * This is a standalone function that can be used directly with AsyncPaginate
 *
 * @param {string} search - Search query
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - Additional data (pagination token)
 * @param {Object} config - Configuration options
 * @returns {Promise<Object>} - { options, hasMore, additional }
 */
export const loadLocationOptions = async (search, loadedOptions, additional, config = {}) => {
  const { pageSize = 20, contactStatus = 'ACTIVE', is_court_location = true, is_event_location = true } = config;

  try {
    const pagination_token = additional?.nextToken || '';

    const result = await fetchLocationsPage({
      pageSize,
      pagination_token,
      searchQuery: search,
      contactStatus,
      is_court_location,
      is_event_location,
    });

    return result;
  } catch (error) {
    console.error('Error loading location options:', error);
    return {
      options: [],
      hasMore: false,
      additional: { nextToken: '' },
    };
  }
};
